import React, { useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { COLORS, STRINGS } from "../../constants";

/**
 * WebDateRangePicker component for selecting a start and end date.
 *
 * @param {Object} props
 * @param {Date} props.fromDate - Start date of the range.
 * @param {Date} props.toDate - End date of the range.
 * @param {Function} props.onFromDateChange - Callback when fromDate changes.
 * @param {Function} props.onToDateChange - Callback when toDate changes.
 * @returns {JSX.Element}
 */
const WebDateRangePicker = ({ fromDate, toDate, onFromDateChange, onToDateChange }) => {
  const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
    <div
      style={styles.inputWrapper}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      role="button"
      tabIndex={0}
      ref={ref}
    >
      <input
        type="text"
        value={value || ""}
        placeholder={value ? undefined : placeholder}
        readOnly
        tabIndex="-1"
        style={styles.input}
        aria-label="Date Picker Input"
      />
      <FontAwesomeIcon icon={faCalendarAlt} style={styles.icon} />
    </div>
  ));

  CustomInput.displayName = "CustomDateInput";

  const handleFromDateChange = useCallback(
    (date) => {
      onFromDateChange(date);

      if (!toDate || toDate < date) {
        onToDateChange(date);
      }
    },
    [toDate, onFromDateChange, onToDateChange]
  );

  const handleToDateChange = useCallback(
    (date) => {
      if (!fromDate || date < fromDate) {
        onFromDateChange(date);
      }
      onToDateChange(date);
    },
    [fromDate, onFromDateChange, onToDateChange]
  );

  return (
    <div style={styles.container}>
      <DatePicker
        selected={fromDate}
        onChange={handleFromDateChange}
        dateFormat="MM/dd/yyyy"
        customInput={<CustomInput />}
        minDate={new Date()}
        withPortal
        portalId="from-date-picker"
        placeholderText={"Any date"}
      />

      <span style={styles.andText}>{STRINGS.and}</span>

      <DatePicker
        selected={toDate}
        onChange={handleToDateChange}
        dateFormat="MM/dd/yyyy"
        customInput={<CustomInput />}
        minDate={fromDate && fromDate > new Date() ? fromDate : new Date()}
        withPortal
        portalId="to-date-picker"
        placeholderText={!toDate ? "Any date" : undefined}
      />
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: "4px",
    backgroundColor: COLORS.textLight,
    padding: "12px 16px",
    cursor: "pointer",
    position: "relative",
    minWidth: "150px",
  },
  input: {
    flex: 1,
    fontSize: "16px",
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    paddingRight: "40px",
  },
  icon: {
    position: "absolute",
    right: "12px",
    fontSize: "18px",
    color: COLORS.border,
    pointerEvents: "none",
  },
  andText: {
    fontSize: "16px",
    color: COLORS.textDark,
  },
};

export default WebDateRangePicker;
